import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDataProvider, useNotify, usePermissions } from 'react-admin';
import { ConfirmationDialog, ParameterInputFactory } from '../../../components/commands';
import { TerminalFeedback } from '../../../components/commands/TerminalFeedback';
import { COMMAND_PERMISSIONS, canExecuteDangerLevel } from '../../../constants/Permissions';
import { Command, CommandExecution, CommandParameter } from '../../../types/commands';

interface CommandExecutionFormProps {
  command: Command;
  unitId: number;
  /** Unit hostname for feedback terminal */
  unitHostname?: string;
  open: boolean;
  onClose: () => void;
  onSuccess: (execution: CommandExecution) => void;
}

/**
 * Form for executing a command with parameters
 *
 * Flow:
 * 1. Show parameter form (if needed)
 * 2. Validate parameters
 * 3. Show confirmation dialog
 * 4. Execute command
 * 5. Show success/error (or terminal feedback if enabled)
 */
export const CommandExecutionForm: React.FC<CommandExecutionFormProps> = ({
  command,
  unitId,
  unitHostname,
  open,
  onClose,
  onSuccess,
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { permissions, isLoading: permissionsLoading } = usePermissions();
  const [parameters, setParameters] = useState<CommandParameter[]>([]);
  const [loadingParameters, setLoadingParameters] = useState(false);
  const [paramValues, setParamValues] = useState<Record<string, any>>({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showTerminal, setShowTerminal] = useState(false);
  const [executionData, setExecutionData] = useState<CommandExecution | null>(null);

  // Check if user can execute this command based on danger level
  const canExecute = !permissionsLoading && canExecuteDangerLevel(permissions, command.danger_level);

  // Get permission error message
  const getPermissionError = (): string | null => {
    if (permissionsLoading) return null;

    const hasExecutePermission = permissions?.includes(COMMAND_PERMISSIONS.EXECUTE_COMMANDS);
    if (!hasExecutePermission) {
      return "You don't have permission to execute commands";
    }

    const dangerLevel = command.danger_level?.toLowerCase();
    if (dangerLevel === 'critical') {
      const hasCriticalPerm = permissions?.includes(COMMAND_PERMISSIONS.EXECUTE_CRITICAL_COMMANDS);
      if (!hasCriticalPerm) {
        return "You don't have permission to execute critical commands";
      }
    } else if (dangerLevel === 'high') {
      const hasHighRiskPerm = permissions?.includes(COMMAND_PERMISSIONS.EXECUTE_HIGH_RISK_COMMANDS);
      if (!hasHighRiskPerm) {
        return "You don't have permission to execute high-risk commands";
      }
    }

    return null;
  };

  const permissionError = getPermissionError();

  // Load parameters when dialog opens and command requires parameters
  useEffect(() => {
    if (open && command.requires_parameters) {
      loadParameters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, command.id]);

  const loadParameters = async () => {
    try {
      setLoadingParameters(true);
      const { data } = await dataProvider.getCommandParameters(command.id);
      setParameters(data);
    } catch (err: any) {
      notify('Failed to load command parameters', { type: 'error' });
      setParameters([]);
    } finally {
      setLoadingParameters(false);
    }
  };

  // Sort parameters by order
  const sortedParameters = [...parameters].sort((a, b) => a.order - b.order);

  const handleParamChange = (paramName: string, value: any) => {
    setParamValues((prev) => ({
      ...prev,
      [paramName]: value,
    }));
    // Clear error for this field when user changes it
    if (errors[paramName]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[paramName];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    sortedParameters.forEach((param) => {
      if (param.is_required && !paramValues[param.name]) {
        newErrors[param.name] = `${param.display_name} is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    // Show confirmation if required
    if (command.confirmation_required) {
      setShowConfirmation(true);
    } else {
      await executeCommand();
    }
  };

  const executeCommand = async () => {
    try {
      setExecuting(true);
      const { data } = await dataProvider.executeCommand(unitId, command.id, paramValues);
      // Check if command has feedback enabled and terminal should be shown
      console.debug('Command execution response:', [
        command.feedback,
        command.feedback_config?.show_terminal,
        unitHostname,
      ]);
      if (command.feedback && command.feedback_config?.show_terminal && unitHostname) {
        setExecutionData(data);
        setShowTerminal(true);
      } else {
        // Standard execution without terminal feedback
        notify(`Command "${command.display_name || command.name}" executed successfully`, { type: 'success' });
        onSuccess(data);
        handleClose();
      }
    } catch (err: any) {
      console.error('Command execution error:', err);

      // Extract error message from various possible error structures
      let errorMessage = 'Failed to execute command';

      if (err?.response?.data) {
        // Axios error response - check multiple possible fields
        const responseData = err.response.data;
        errorMessage =
          responseData?.message ||
          responseData?.error ||
          responseData?.errors?.[0]?.message || // Laravel validation errors
          (typeof responseData === 'string' ? responseData : errorMessage);
      } else if (err?.body?.message) {
        // React Admin HttpError
        errorMessage = err.body.message;
      } else if (err?.message && !err.message.startsWith('Request failed with status code')) {
        // Standard Error (but skip generic Axios messages)
        errorMessage = err.message;
      }

      // Add status code if available for better debugging
      if (err?.response?.status) {
        errorMessage = `${errorMessage} (HTTP ${err.response.status})`;
      }

      notify(errorMessage, { type: 'error', autoHideDuration: 300000 });
    } finally {
      setExecuting(false);
      setShowConfirmation(false);
    }
  };

  const handleClose = () => {
    setParameters([]);
    setParamValues({});
    setErrors({});
    setShowConfirmation(false);
    onClose();
  };

  const handleConfirmationCancel = () => {
    setShowConfirmation(false);
  };

  const handleTerminalClose = () => {
    setShowTerminal(false);
    if (executionData) {
      onSuccess(executionData);
    }
  };

  return (
    <>
      <Dialog open={open && !showConfirmation} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Execute: {command.display_name || command.name}</DialogTitle>
        <DialogContent>
          {permissionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {permissionError}
            </Alert>
          )}

          {loadingParameters ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : command.requires_parameters && sortedParameters.length > 0 ? (
            <Box sx={{ pt: 2 }}>
              {sortedParameters.map((param) => (
                <Box key={param.id} sx={{ mb: 2 }}>
                  <ParameterInputFactory
                    parameter={param}
                    value={paramValues[param.name] ?? param.default_value ?? ''}
                    onChange={(value) => handleParamChange(param.name, value)}
                    error={errors[param.name]}
                  />
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ pt: 2, pb: 2 }}>
              <p>This command does not require any parameters.</p>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={executing}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={executing || loadingParameters || !canExecute}>
            {command.confirmation_required ? 'Next' : 'Execute'}
          </Button>
        </DialogActions>
      </Dialog>

      {showConfirmation && (
        <ConfirmationDialog
          open={showConfirmation}
          onClose={handleConfirmationCancel}
          onConfirm={executeCommand}
          title={`Execute: ${command.display_name || command.name}`}
          message={command.description}
          dangerLevel={command.danger_level}
          isExecuting={executing}
        />
      )}

      <TerminalFeedback
        open={showTerminal}
        onClose={handleTerminalClose}
        hostname={unitHostname || ''}
        executionUuid={executionData?.uuid || ''}
        executionId={executionData?.id || 0}
        commandName={command.display_name || command.name}
        commandSlug={command.name}
        feedbackConfig={command.feedback_config}
      />
    </>
  );
};
